import * as Yup from 'yup';
import { optionValidationSchema } from './optionSchema';

export const questionValidationSchema = Yup.object({
  pickem_id: Yup.string().required('Pickem is required'),
  question_text_en: Yup.string()
    .required('English question text is required')
    .min(5, 'English question must be at least 5 characters')
    .max(500, 'English question must be less than 500 characters'),
  question_text_fr: Yup.string()
    .required('French question text is required')
    .min(5, 'French question must be at least 5 characters')
    .max(500, 'French question must be less than 500 characters'),
  question_subtext_en: Yup.string().max(500, 'Subtext must be less than 500 characters').nullable(),
  question_subtext_fr: Yup.string().max(500, 'Subtext must be less than 500 characters').nullable(),
  question_image_en: Yup.string().url('Must be a valid URL').nullable(),
  question_image_fr: Yup.string().url('Must be a valid URL').nullable(),
  start_utc: Yup.string().nullable(),
  question_type: Yup.string().required('Question type is required'),
  points: Yup.number().min(0, 'Points must be 0 or greater').required('Points are required'),
  sportcc_match_id: Yup.number().nullable(),
  sportcc_match_data: Yup.object()
    .shape({
      homeTeam: Yup.object().shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
      }),
      awayTeam: Yup.object().shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
      }),
      league: Yup.string().required(),
      matchDate: Yup.string().required(),
      status: Yup.string().required(),
    })
    .nullable()
    .when('sportcc_match_id', {
      is: (id: number | null) => id !== null && id !== undefined,
      then: schema => schema.required('Match data is required when match is selected'),
      otherwise: schema => schema.nullable(),
    }),
  options: Yup.array()
    .of(optionValidationSchema)
    .when('question_type', {
      is: (type: string) => type === 'tiebreaker',
      then: schema => schema.notRequired(),
      otherwise: schema =>
        schema
          .min(2, 'At least 2 options are required')
          .required('Options are required')
          .test(
            'max-one-correct',
            'No more than one option can be marked as correct',
            function (options) {
              if (!options) return false;
              const correctCount = options.filter(opt => opt.is_correct).length;
              return correctCount <= 1;
            }
          ),
    })
    .test(
      'player-grading-validation',
      'Player selection required for player-based questions',
      function (options) {
        const { question_metadata, sportcc_match_id } = this.parent;

        // Only validate if SportCC match is linked
        if (!sportcc_match_id || !question_metadata?.grading_type) {
          return true;
        }

        const gradingType = question_metadata.grading_type;
        const requiresPlayer = [
          'player_goals_over_under',
          'player_assists_over_under',
          'player_points_over_under',
          'grouped_player_most_points',
        ].includes(gradingType);

        if (!requiresPlayer || !options) {
          return true;
        }

        // Check each option has player_id
        const invalidOptions = options.filter((opt: any) => !opt.grading_data?.playerId);

        if (invalidOptions.length > 0) {
          return this.createError({
            path: `options[${options.indexOf(invalidOptions[0])}].grading_data.playerId`,
            message: 'Player selection is required for this grading type',
          });
        }

        return true;
      }
    ),
  question_metadata: Yup.object()
    .shape({
      grading_type: Yup.string().nullable(),
      config: Yup.object()
        .shape({
          threshold: Yup.number().nullable(),
          comparison: Yup.string().oneOf(['over', 'under']).nullable(),
        })
        .nullable(),
    })
    .nullable(),
});
